import { useEffect, useMemo, useRef, useState } from "react";
import { Bot, Send, Loader2, Sparkles, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  useConversationMessages,
  type CopilotMessage,
} from "@/hooks/useCopilotConversations";

interface CopilotPaneProps {
  companyId: string | null;
  siteId?: string | null;
  conversationId: string | null;
  onConversationCreated: (id: string) => void;
}

interface UiMessage {
  role: "user" | "assistant";
  content: string;
  pending?: boolean;
}

const SUGGESTED = [
  "What's our current safety status?",
  "Show me the top friction points",
  "Which teams have the highest risk?",
  "How effective are our interventions?",
  "Give me a weekly safety briefing",
];

// ---------- Rich answer rendering ----------
interface MetricSpec {
  label: string;
  value: string | number;
  delta?: string;
  tone?: "positive" | "negative" | "neutral";
  hint?: string;
}

const MetricCard = ({ spec }: { spec: MetricSpec }) => {
  const tone = spec.tone || "neutral";
  const ToneIcon = tone === "positive" ? TrendingUp : tone === "negative" ? TrendingDown : Minus;
  const toneClass =
    tone === "positive"
      ? "text-success"
      : tone === "negative"
      ? "text-destructive"
      : "text-muted-foreground";
  return (
    <div className="rounded-xl border bg-card p-4 min-w-[160px]">
      <p className="text-xs text-muted-foreground mb-1">{spec.label}</p>
      <p className="text-2xl font-bold tracking-tight">{spec.value}</p>
      {(spec.delta || spec.hint) && (
        <div className={cn("flex items-center gap-1 mt-1 text-xs", toneClass)}>
          {spec.delta && <ToneIcon className="h-3 w-3" />}
          <span>{spec.delta || spec.hint}</span>
        </div>
      )}
    </div>
  );
};

// Parse content for ```metric ... ``` blocks and split into segments
type Segment =
  | { type: "md"; content: string }
  | { type: "metric"; specs: MetricSpec[] };

const parseSegments = (content: string): Segment[] => {
  const segments: Segment[] = [];
  const re = /```metric\s*([\s\S]*?)```/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    if (m.index > last) {
      segments.push({ type: "md", content: content.slice(last, m.index) });
    }
    try {
      const parsed = JSON.parse(m[1].trim());
      const specs: MetricSpec[] = Array.isArray(parsed) ? parsed : [parsed];
      segments.push({ type: "metric", specs });
    } catch {
      // If JSON invalid, fall back to showing raw
      segments.push({ type: "md", content: "```\n" + m[1] + "\n```" });
    }
    last = m.index + m[0].length;
  }
  if (last < content.length) {
    segments.push({ type: "md", content: content.slice(last) });
  }
  return segments;
};

const AssistantContent = ({ content }: { content: string }) => {
  const segments = useMemo(() => parseSegments(content), [content]);
  return (
    <div className="space-y-3">
      {segments.map((seg, i) =>
        seg.type === "metric" ? (
          <div key={i} className="flex flex-wrap gap-3">
            {seg.specs.map((s, j) => (
              <MetricCard key={j} spec={s} />
            ))}
          </div>
        ) : (
          <div
            key={i}
            className="prose prose-sm dark:prose-invert max-w-none
              prose-headings:font-semibold prose-headings:mt-4 prose-headings:mb-2
              prose-h1:text-lg prose-h2:text-base prose-h3:text-sm
              prose-p:my-2 prose-p:leading-relaxed
              prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5
              prose-table:text-xs prose-th:bg-muted prose-th:px-2 prose-th:py-1
              prose-td:px-2 prose-td:py-1 prose-td:border-t
              prose-blockquote:border-l-primary prose-blockquote:bg-primary/5
              prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded
              prose-code:before:content-none prose-code:after:content-none"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{seg.content}</ReactMarkdown>
          </div>
        )
      )}
    </div>
  );
};
// ---------- end rich rendering ----------

export const CopilotPane = ({
  companyId,
  siteId,
  conversationId,
  onConversationCreated,
}: CopilotPaneProps) => {
  const qc = useQueryClient();
  const { data: persistedMessages = [] } = useConversationMessages(conversationId);
  const [localMessages, setLocalMessages] = useState<UiMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  // Reset local state when conversation changes
  useEffect(() => {
    setLocalMessages([]);
  }, [conversationId]);

  const messages: UiMessage[] = conversationId
    ? [
        ...(persistedMessages as CopilotMessage[]).map((m) => ({
          role: m.role,
          content: m.content,
        })),
        ...localMessages,
      ]
    : localMessages;

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, isLoading]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || isLoading) return;
    const userMsg = text.trim();
    setInput("");
    setLocalMessages((prev) => [
      ...prev,
      { role: "user", content: userMsg },
      { role: "assistant", content: "", pending: true },
    ]);
    setIsLoading(true);

    try {
      const history = messages.slice(-10).map((m) => ({ role: m.role, content: m.content }));
      const { data, error } = await supabase.functions.invoke("safety-copilot-query", {
        body: {
          query: userMsg,
          siteId: siteId ?? null,
          companyId,
          conversationHistory: history,
          conversationId,
          persist: true,
        },
      });

      let replyText = "Sorry, I could not generate a response.";
      let newConvId: string | null = conversationId;

      if (error) {
        console.error("[copilot] invoke error:", error);
        if (error.message?.includes("limitExceeded") || error.message?.includes("AI_LIMIT_EXCEEDED")) {
          replyText = "⚠️ Batas percakapan AI bulanan sudah tercapai. Hubungi admin untuk upgrade atau top-up.";
        } else {
          replyText = "Sorry, I encountered an error. Please try again.";
          toast.error("Copilot request failed", { description: error.message });
        }
      } else {
        replyText = data?.response || replyText;
        newConvId = data?.conversationId ?? conversationId;
        if (!newConvId) {
          // Persist failed silently in the function; surface so user knows
          console.warn("[copilot] no conversationId returned — chat won't appear in history");
        }
      }

      // If we just created a conversation, swap to its persisted view
      if (!conversationId && newConvId) {
        setLocalMessages([]);
        onConversationCreated(newConvId);
        qc.invalidateQueries({ queryKey: ["copilot-conversations"] });
        qc.invalidateQueries({ queryKey: ["copilot-messages", newConvId] });
      } else {
        setLocalMessages((prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i--) {
            if (next[i].pending) {
              next[i] = { role: "assistant", content: replyText };
              break;
            }
          }
          return next;
        });
        if (conversationId) {
          qc.invalidateQueries({ queryKey: ["copilot-conversations"] });
          qc.invalidateQueries({ queryKey: ["copilot-messages", conversationId] });
          setTimeout(() => setLocalMessages([]), 400);
        }
      }
    } catch (err: any) {
      console.error("[copilot] unexpected error:", err);
      toast.error("Copilot error", { description: err?.message || "Something went wrong" });
      setLocalMessages((prev) =>
        prev.map((m) =>
          m.pending ? { role: "assistant", content: "Sorry, something went wrong." } : m
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const empty = messages.length === 0;

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] max-h-[calc(100vh-8rem)]">
      {empty ? (
        <div className="flex-1 flex flex-col items-center justify-center px-6 max-w-2xl mx-auto w-full">
          <div className="p-4 rounded-2xl bg-primary/10 mb-4">
            <Bot className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-2xl font-semibold mb-2 text-center">AI Safety Copilot</h2>
          <p className="text-muted-foreground text-center mb-8">
            Ask anything about your safety data — risks, reports, workers, interventions.
          </p>
          <div className="w-full mb-6">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(input);
                  }
                }}
                placeholder="Ask about safety metrics, risks, trends…"
                className="flex-1 h-12 rounded-xl"
              />
              <Button
                size="lg"
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || isLoading}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            {SUGGESTED.map((q) => (
              <Badge
                key={q}
                variant="secondary"
                className="cursor-pointer hover:bg-secondary/80 py-1.5 px-3"
                onClick={() => sendMessage(q)}
              >
                {q}
              </Badge>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6">
            <div className="max-w-3xl mx-auto space-y-4">
              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}
                >
                  <div
                    className={cn(
                      m.role === "user"
                        ? "max-w-[85%] rounded-2xl rounded-br-md px-4 py-3 bg-primary text-primary-foreground"
                        : "max-w-[95%] w-full rounded-2xl rounded-bl-md px-4 py-3 bg-muted"
                    )}
                  >
                    {m.role === "assistant" && (
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="h-3 w-3 text-primary" />
                        <span className="text-xs font-medium text-primary">Copilot</span>
                      </div>
                    )}
                    {m.pending ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Analyzing data…
                      </div>
                    ) : m.role === "assistant" ? (
                      <AssistantContent content={m.content} />
                    ) : (
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.content}</p>
                    )}
                  </div>
                </motion.div>
              ))}
              <div ref={endRef} />
            </div>
          </div>
          <div className="border-t bg-background/80 backdrop-blur p-4">
            <div className="max-w-3xl mx-auto flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(input);
                  }
                }}
                placeholder="Reply to copilot…"
                className="flex-1 h-11 rounded-xl"
                disabled={isLoading}
              />
              <Button onClick={() => sendMessage(input)} disabled={!input.trim() || isLoading}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
